/**
 * Shared employee slot overlap rules for storefront and portal booking.
 * When double booking is off, each staff member may have at most one overlapping
 * appointment at a time (any service).
 */

export function getDoubleBookingSettings(companyData = {}) {
  const settings = companyData.settings || {};
  const rawMax = settings.maxConcurrentAppointmentsPerSlot;
  const parsedMax =
    typeof rawMax === 'number' ? rawMax : rawMax != null ? parseInt(rawMax, 10) : NaN;

  return {
    allowDoubleBooking: settings.allowDoubleBooking === true,
    maxConcurrentPerSlot:
      Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null,
  };
}

export function resolveAppointmentEmployeeId(data = {}) {
  if (!data) return null;
  return (
    data.employeeId ||
    data.staffId ||
    data.employee?.assignedStaffId ||
    data.employee?.id ||
    null
  );
}

export function toJsDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function computeAppointmentDurationMinutes(data = {}) {
  const storedDuration = parseInt(data.duration, 10);
  if (Number.isFinite(storedDuration) && storedDuration > 0) {
    return storedDuration;
  }

  const serviceDuration = parseInt(data.serviceDuration, 10) || 0;
  const serviceBufferTime = parseInt(data.serviceBufferTime, 10) || 0;
  const addons = data.selectedAddons || data.addons || [];
  const addonsDuration = addons.reduce(
    (total, addon) =>
      total +
      (parseInt(addon.duration, 10) || 0) +
      (parseInt(addon.bufferTime, 10) || 0),
    0
  );

  const computed = serviceDuration + serviceBufferTime + addonsDuration;
  return computed > 0 ? computed : 60;
}

export function getAppointmentTimeRange(data) {
  const start = toJsDate(data.dateTime);
  if (!start) return null;

  let end = toJsDate(data.endTime);
  if (!end) {
    end = new Date(start);
    end.setMinutes(end.getMinutes() + computeAppointmentDurationMinutes(data));
  }

  return { start, end };
}

export function timesOverlap(slotStart, slotEnd, aptStart, aptEnd) {
  return slotStart < aptEnd && slotEnd > aptStart;
}

export function countOverlappingAppointments({
  appointments,
  employeeId,
  slotStart,
  slotEnd,
}) {
  if (!employeeId) return 0;

  let count = 0;
  for (const apt of appointments) {
    const aptEmployeeId = resolveAppointmentEmployeeId(apt);
    if (aptEmployeeId !== employeeId) continue;

    const range = getAppointmentTimeRange(apt);
    if (!range) continue;

    if (timesOverlap(slotStart, slotEnd, range.start, range.end)) {
      count += 1;
    }
  }
  return count;
}

export function canBookWithOverlapCount(
  overlappingCount,
  { allowDoubleBooking, maxConcurrentPerSlot }
) {
  if (!allowDoubleBooking) {
    return overlappingCount === 0;
  }
  if (maxConcurrentPerSlot == null) {
    return true;
  }
  return overlappingCount < maxConcurrentPerSlot;
}

export function isEmployeeSlotAvailable({
  appointments,
  employeeId,
  slotStart,
  slotEnd,
  allowDoubleBooking,
  maxConcurrentPerSlot,
}) {
  const overlappingCount = countOverlappingAppointments({
    appointments,
    employeeId,
    slotStart,
    slotEnd,
  });
  return canBookWithOverlapCount(overlappingCount, {
    allowDoubleBooking,
    maxConcurrentPerSlot,
  });
}

export function isAnyEmployeeSlotAvailable({
  appointments,
  employeeIds,
  slotStart,
  slotEnd,
  allowDoubleBooking,
  maxConcurrentPerSlot,
}) {
  const ids = Array.isArray(employeeIds) ? employeeIds.filter(Boolean) : [];
  return ids.some((employeeId) =>
    isEmployeeSlotAvailable({
      appointments,
      employeeId,
      slotStart,
      slotEnd,
      allowDoubleBooking,
      maxConcurrentPerSlot,
    })
  );
}

export function normalizeDayAppointmentDoc(docSnap) {
  const data = docSnap.data();
  const range = getAppointmentTimeRange(data);
  if (!range) return null;

  return {
    ...data,
    id: docSnap.id,
    dateTime: range.start,
    endTime: range.end,
    employeeId: resolveAppointmentEmployeeId(data),
  };
}
